import { fetchHubStats, MIN_HUB_ARTICLES } from "@/lib/hubArticles";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import Category from "@/pages/Category";
import { categorySubLinks, fetchCategoryArticleLinks } from "@/lib/listingLinks";
import { resolveCategoryRedirect } from "@/lib/categoryRedirects";
import { categoryConfig } from "@/lib/categoryConfig";
import { supabase } from "@/integrations/supabase/client";
import { cached } from "@/lib/ssrCache";
import { getSearchDescription } from "@/lib/searchDescriptions";

const SITE = "https://www.garden-review.co.uk";

async function fetchCategoryDescription(slug: string): Promise<string | null> {
  return cached(`catdesc:${slug}`, 60 * 60 * 1000, async () => {
    try {
      const { data } = await supabase.from("categories").select("description").eq("slug", slug).maybeSingle();
      return data?.description?.trim() || null;
    } catch {
      return null;
    }
  });
}

export const Route = createFileRoute("/category/$categorySlug")({
  // Resolve legacy/alias category slugs on the server so crawlers get a real
  // 301 instead of a client-rendered "Category Not Found" shell.
  beforeLoad: ({ params, location }) => {
    const sub = (location.search as Record<string, unknown> | undefined)?.sub;
    const target = resolveCategoryRedirect(
      params.categorySlug,
      typeof sub === "string" ? sub : null,
    );
    if (target) throw redirect({ href: target, statusCode: 301 });
    // Unknown slug → real HTTP 404 with the NotFound page.
    if (!categoryConfig[params.categorySlug]) throw notFound();
  },
  loader: async ({ params }) => {
    const [ssrLinks, dbDescription, hubStats] = await Promise.all([
      fetchCategoryArticleLinks(params.categorySlug, 60),
      fetchCategoryDescription(params.categorySlug),
      fetchHubStats().catch(() => ({}) as Record<string, { count: number }>),
    ]);
    // Hide product hubs that are still too thin to index; they reappear at MIN_HUB_ARTICLES.
    const ssrSubLinks = categorySubLinks(params.categorySlug).filter((l) => {
      const hub = l.path.replace(/^\//, "");
      const stat = hubStats[hub];
      return !stat || stat.count >= MIN_HUB_ARTICLES;
    });
    return { ssrLinks, ssrSubLinks, dbDescription };
  },
  head: ({ params, loaderData }) => {
    const config = categoryConfig[params.categorySlug];
    if (!config) return {};
    // Filtered category views keep the parent canonical, but a moved parent
    // must point directly at its current destination rather than its 301 alias.
    const canonicalPath = resolveCategoryRedirect(params.categorySlug, null) || `/category/${params.categorySlug}`;
    const url = `${SITE}${canonicalPath}`;
    const title = `${config.title} | Garden-Review`;
    const description = getSearchDescription(`/category/${params.categorySlug}`,
      loaderData?.dbDescription ||
      config.description ||
      `Expert ${config.title.toLowerCase()} reviews, buying guides and practical advice for UK gardeners from Garden Review.`);
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: config.title, item: url },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumb).replace(/</g, "\\u003c"),
        },
      ],
    };
  },
  component: CategoryRoute,
});

function CategoryRoute() {
  const { ssrLinks, ssrSubLinks } = Route.useLoaderData();
  return <Category ssrLinks={ssrLinks} ssrSubLinks={ssrSubLinks} />;
}
